import { useEffect } from 'react';

import type { RefObject } from 'react';

interface Options {
  enabled: boolean;
  property?: 'height' | 'maxHeight';
  round?: boolean;
}

export const useSyncMaxHeight = (
  sourceRef: RefObject<HTMLElement>,
  targetRef: RefObject<HTMLElement>,
  { enabled, property = 'maxHeight', round = true }: Options,
): void => {
  useEffect(() => {
    if (!enabled) return;

    let rafId = 0;
    let cleanupObservers: (() => void) | null = null;

    const setup = (): void => {
      const sourceEl = sourceRef.current;
      const targetEl = targetRef.current;

      if (!sourceEl || !targetEl) {
        // Wait until both elements are present
        rafId = requestAnimationFrame(setup);
        return;
      }

      const update = (): void => {
        if (!sourceEl || !targetEl) return;
        const h = sourceEl.getBoundingClientRect().height;
        if (h > 0) {
          const val = round ? Math.round(h) : h;
          targetEl.style[property] = `${val}px`;
        }
      };

      update();

      const ro = new ResizeObserver(update);
      ro.observe(sourceEl);

      const mo = new MutationObserver(update);
      mo.observe(sourceEl, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });

      const onResize = (): void => update();
      // eslint-disable-next-line react-web-api/no-leaked-event-listener
      window.addEventListener('resize', onResize);

      cleanupObservers = (): void => {
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener('resize', onResize);
      };
    };

    rafId = requestAnimationFrame(setup);

    return (): void => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (cleanupObservers) {
        cleanupObservers();
      }
    };
  }, [enabled, sourceRef, targetRef, property, round]);
};
